import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
 

const services = [
  {
    icon: 'fa-solid fa-arrows-split-up-and-left',
    title: '1. ETL & ELT Pipeline Engineering',
    desc: 'Extract, transform, and load enterprise data from disparate sources into centralized repositories with high-throughput, low-latency automated pipelines.',
  },
  {
    icon: 'fa-solid fa-database',
    title: '2. Cloud Data Warehousing',
    desc: 'Architect and consolidate enterprise data structures into unified cloud platforms like Snowflake, BigQuery, and AWS Redshift.',
  },
  {
    icon: 'fa-solid fa-plug-circle-bolt',
    title: '3. API & Middleware Integration',
    desc: 'Connect SaaS platforms, legacy internal systems, and third-party tools via high-speed, secure RESTful and GraphQL APIs.',
  },
  {
    icon: 'fa-solid fa-bolt',
    title: '4. Real-Time Data Streaming',
    desc: 'Enable event-driven architectures with Apache Kafka and RabbitMQ for real-time analytics, event processing, and instant operational feedback.',
  },
  {
    icon: 'fa-solid fa-broom',
    title: '5. Data Cleansing & Governance',
    desc: 'Standardize incoming data flows, remove duplicates, enforce schema validation, and ensure compliance with GDPR and HIPAA regulations.',
  },
  {
    icon: 'fa-solid fa-diagram-project',
    title: '6. Master Data Management (MDM)',
    desc: 'Build a single source of truth across customer, product, and financial entities to eliminate operational silos across departments.',
  },
  {
    icon: 'fa-solid fa-cloud-arrow-down',
    title: '7. Hybrid & Multi-Cloud Data Migration',
    desc: 'Migrate on-premises SQL/NoSQL databases seamlessly into modern hybrid-cloud and multi-cloud environments without disrupting daily operations.',
  },
  {
    icon: 'fa-solid fa-chart-pie',
    title: '8. BI & Analytics Integration',
    desc: 'Pipeline aggregated data into business intelligence platforms like Power BI, Tableau, and Looker for automated executive dashboards.',
  },
  {
    icon: 'fa-solid fa-network-wired',
    title: '9. Enterprise Service Bus (ESB) Setup',
    desc: 'Deploy resilient ESB and messaging layers to decouple complex software ecosystems and enable smooth inter-system communication.',
  },
  {
    icon: 'fa-solid fa-shield-cat',
    title: '10. Data Security & Encryption',
    desc: 'Protect data in transit and at rest with end-to-end encryption, role-based access control (RBAC), and automated audit logs.',
  },
];

const whyChooseUs = [
  'Unified single source of truth for enterprise operations',
  'Real-time streaming and batch pipeline capabilities',
  'Scalable cloud-native warehouse architectures',
  'Strict compliance with GDPR, HIPAA, and SOC2',
  'Robust error-handling and automated data recovery',
  'Reduced operational silos across departments',
  'Seamless connection between legacy and SaaS systems',
  'High-throughput, ultra-low latency processing',
  'Transparent auditability and lineage tracking',
  'End-to-end support and ongoing monitoring',
];

const processSteps = [
  { step: '01', title: 'Data Audit & Discovery', desc: 'Identify data sources, schema types, security rules, and business throughput requirements.' },
  { step: '02', title: 'Architecture Blueprinting', desc: 'Design custom ETL/ELT pipelines, target storage models, and system-to-system mapping.' },
  { step: '03', title: 'Pipeline Development', desc: 'Engineer automated data flows, API connectors, and transformation rules using modern frameworks.' },
  { step: '04', title: 'Cleansing & Validation', desc: 'Apply deduplication, schema checks, and encryption standards to guarantee data integrity.' },
  { step: '05', title: 'Testing & Cutover', desc: 'Conduct load testing, stress-test pipelines, and perform live data synchronization with zero data loss.' },
  { step: '06', title: 'Monitoring & Analytics', desc: 'Set up real-time telemetry, pipeline monitoring, and continuous optimization dashboards.' },
];

const techStack = [
  { category: 'Data Warehousing', items: ['Snowflake', 'Google BigQuery', 'AWS Redshift', 'Databricks'] },
  { category: 'ETL & Integration', items: ['Apache NiFi', 'Airflow', 'Talend', 'Informatica', 'Fivetran'] },
  { category: 'Streaming & Messaging', items: ['Apache Kafka', 'RabbitMQ', 'AWS Kinesis', 'Azure Event Hubs'] },
  { category: 'Databases & Storage', items: ['PostgreSQL', 'MongoDB', 'Redis', 'AWS S3', 'Azure Blob'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const DataIntegration = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Data Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Data <span className="gradient-text-cyan">Integration</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we break down data silos and unify enterprise ecosystems. Streamline your data pipelines, automate ETL workflows, and transform raw metrics into real-time business intelligence.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Integrate Your Data With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Data Integration Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Data Tech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Data Architecture Model</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Unify Your Enterprise Data with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Turn scattered, disconnected data silos into a powerful, real-time analytics asset with Lidlbritain data engineering experts.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Data Engineer &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DataIntegration;